import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { RestaurantTableService } from '../../../services/restaurantTable.service';
import { AddComandaItemDto, ComandaService, CreateComandaDto } from '../../../services/comanda.service';
import { RestaurantTable } from '../../../models/restaurantTable.model';
import { Comanda, ComandaItemResponseDto } from '../../../models/comanda.model';
import { CreateSaleFromPdvDto } from '../create-sale-from-pdv-dto';
import { RestResponse } from '../../../models/restResponse.mode';
import { Product } from '../../../models/product.model';
import { ProductService } from '../../../services/product.service';

export interface TableWithComanda {
  table: RestaurantTable;
  comandaId: number | null;
  /** Total da comanda quando mesa ocupada (para exibir no quadrado). */
  comandaTotal?: number;
  /** Observação/nome do cliente da comanda quando ocupada. */
  comandaObservation?: string | null;
}

@Component({
  selector: 'app-pdv-tables-view',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, DialogModule, TableModule],
  templateUrl: './pdv-tables-view.component.html',
  styleUrls: ['./pdv-tables-view.component.css'],
})
export class PdvTablesViewComponent implements OnInit {
  @Output() closeComandaRequest = new EventEmitter<{
    sale: CreateSaleFromPdvDto;
    comandaId: number;
  }>();
  @Output() closeViewRequest = new EventEmitter<void>();

  tablesEnriched: TableWithComanda[] = [];
  tablesFiltered: TableWithComanda[] = [];
  selectedTable: TableWithComanda | null = null;
  loading = true;
  loadingComanda = false;
  loadingOpenComanda = false;
  loadingAddItem = false;
  loadingProductSearch = false;
  loadingChangeItem = false;
  loadingConsulta = false;
  searchMesa = '';
  /** Modal de consulta: comanda carregada por find-by-table. */
  comandaModalVisible = false;
  comandaForModal: Comanda | null = null;
  mesaNumberForModal = 0;
  addItemsModalVisible = false;
  comandaForEdit: Comanda | null = null;
  productSearch = '';
  productSearchResults: Product[] = [];
  selectedProduct: Product | null = null;
  itemQuantity = 1;
  itemObservation = '';

  constructor(
    private restaurantTableService: RestaurantTableService,
    private comandaService: ComandaService,
    private productService: ProductService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.tablesEnriched = [];
    this.selectedTable = null;

    this.restaurantTableService.list(undefined, 1).subscribe({
      next: (tablesRes) => {
        const tables = tablesRes.data ?? [];
        this.comandaService.list('ABERTA', undefined, 1).subscribe({
          next: (comandasRes) => {
            const comandas = comandasRes.data ?? [];
            this.tablesEnriched = tables.map((table) => {
              const comanda = comandas.find((c) =>
                c.tables?.some((t) => t.id === table.id)
              );
              return {
                table,
                comandaId: comanda ? comanda.id : null,
                comandaTotal: comanda?.total,
                comandaObservation: comanda?.observation ?? null,
              };
            });
            this.applyFilter();
            this.loading = false;
          },
          error: () => {
            this.tablesEnriched = tables.map((t) => ({ table: t, comandaId: null }));
            this.applyFilter();
            this.loading = false;
          },
        });
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  applyFilter(): void {
    const term = (this.searchMesa || '').trim().toLowerCase();
    if (!term) {
      this.tablesFiltered = [...this.tablesEnriched];
      return;
    }
    this.tablesFiltered = this.tablesEnriched.filter(
      (row) => String(row.table.number).toLowerCase().includes(term)
    );
  }

  selectTable(row: TableWithComanda): void {
    this.selectedTable = row;
  }

  isSelected(row: TableWithComanda): boolean {
    return (
      this.selectedTable !== null &&
      this.selectedTable.table.id === row.table.id
    );
  }

  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      DISPONIVEL: 'Livre',
      OCUPADA: 'Consumindo',
      RESERVADA: 'Reservado',
    };
    return map[status] ?? status;
  }

  /** Classes de cor do quadrado por status (Livre=verde, Consumindo=vermelho, Reservado=azul). */
  getBoxClasses(row: TableWithComanda): string {
    const selected = this.isSelected(row);
    const status = row.table.status;
    const ring = selected ? 'ring-2 ring-offset-2 ring-orange-400 shadow-lg' : '';
    switch (status) {
      case 'DISPONIVEL':
        return `bg-emerald-500 hover:bg-emerald-600 text-white ${ring}`;
      case 'OCUPADA':
        return `bg-red-500 hover:bg-red-600 text-white ${ring}`;
      case 'RESERVADA':
        return `bg-blue-500 hover:bg-blue-600 text-white ${ring}`;
      default:
        return `bg-gray-400 hover:bg-gray-500 text-white ${ring}`;
    }
  }

  /** Botão Fechar comanda sempre disponível; ao clicar, busca comanda por mesa e, se houver, transforma em venda. */
  fecharComanda(): void {
    if (!this.selectedTable || this.loadingComanda) return;
    const tableId = this.selectedTable.table.id;
    this.loadingComanda = true;

    this.comandaService.findByTable(tableId).subscribe({
      next: (res: RestResponse<Comanda>) => {
        const comanda = res?.data;
        if (!comanda) {
          alert('Nenhuma comanda aberta para esta mesa.');
          this.loadingComanda = false;
          return;
        }
        const sale = this.mapComandaToSale(comanda);
        this.closeComandaRequest.emit({ sale, comandaId: comanda.id });
        this.loadingComanda = false;
      },
      error: () => {
        alert('Nenhuma comanda aberta para esta mesa.');
        this.loadingComanda = false;
      },
    });
  }

  abrirComanda(): void {
    if (!this.selectedTable || this.loadingOpenComanda) return;
    if (this.selectedTable.table.status !== 'DISPONIVEL') {
      alert('Apenas mesas livres podem abrir comanda.');
      return;
    }

    const dto: CreateComandaDto = {
      idsMesa: [this.selectedTable.table.id],
      observation: null,
    };

    this.loadingOpenComanda = true;
    this.comandaService.create(dto).subscribe({
      next: (res) => {
        const comanda = res?.data;
        if (comanda) {
          this.selectedTable = {
            ...this.selectedTable!,
            table: { ...this.selectedTable!.table, status: 'OCUPADA' },
            comandaId: comanda.id,
            comandaTotal: comanda.total,
            comandaObservation: comanda.observation ?? null,
          };
        }
        this.loadData();
        this.loadingOpenComanda = false;
      },
      error: (err) => {
        alert(err?.error?.message || 'Não foi possível abrir a comanda para esta mesa.');
        this.loadingOpenComanda = false;
      },
    });
  }

  abrirModalAdicionarItens(): void {
    if (!this.selectedTable) return;
    const tableId = this.selectedTable.table.id;
    this.comandaService.findByTable(tableId).subscribe({
      next: (res) => {
        const comanda = res?.data;
        if (!comanda) {
          alert('Nenhuma comanda aberta para esta mesa.');
          return;
        }
        this.comandaForEdit = comanda;
        this.addItemsModalVisible = true;
        this.resetProductSelection();
      },
      error: () => {
        alert('Nenhuma comanda aberta para esta mesa.');
      },
    });
  }

  onProductSearchChange(): void {
    const term = (this.productSearch || '').trim();
    if (term.length < 2) {
      this.productSearchResults = [];
      return;
    }
    this.loadingProductSearch = true;
    this.productService.search(term, true).subscribe({
      next: (res) => {
        this.productSearchResults = res?.data ?? [];
        this.loadingProductSearch = false;
      },
      error: () => {
        this.productSearchResults = [];
        this.loadingProductSearch = false;
      },
    });
  }

  selectProduct(product: Product): void {
    this.selectedProduct = product;
    this.productSearch = `${product.code} - ${product.description}`;
    this.productSearchResults = [];
  }

  adicionarItemComanda(): void {
    if (!this.comandaForEdit || !this.selectedProduct || this.loadingAddItem) return;
    if (this.itemQuantity <= 0) {
      alert('A quantidade deve ser maior que zero.');
      return;
    }

    const dto: AddComandaItemDto = {
      productId: this.selectedProduct.id,
      quantity: this.itemQuantity,
      observation: this.itemObservation?.trim() || null,
    };

    this.loadingAddItem = true;
    this.comandaService.addItem(this.comandaForEdit.id, dto).subscribe({
      next: (res) => {
        this.comandaForEdit = res?.data ?? this.comandaForEdit;
        this.resetProductSelection();
        this.loadingAddItem = false;
        this.refreshTablesData();
      },
      error: (err) => {
        alert(err?.error?.message || 'Não foi possível adicionar o item na comanda.');
        this.loadingAddItem = false;
      },
    });
  }

  removerItemComanda(itemId: number): void {
    if (!this.comandaForEdit || this.loadingChangeItem) return;
    this.loadingChangeItem = true;
    this.comandaService.removeItem(this.comandaForEdit.id, itemId).subscribe({
      next: () => {
        this.reloadEditableComanda();
      },
      error: (err) => {
        alert(err?.error?.message || 'Não foi possível remover o item da comanda.');
        this.loadingChangeItem = false;
      },
    });
  }

  alterarQuantidadeItem(item: ComandaItemResponseDto, quantity: number): void {
    if (!this.comandaForEdit || this.loadingChangeItem) return;
    if (quantity <= 0) {
      this.removerItemComanda(item.id);
      return;
    }
    this.loadingChangeItem = true;
    this.comandaService.removeItem(this.comandaForEdit.id, item.id).subscribe({
      next: () => {
        const dto: AddComandaItemDto = {
          productId: item.productId,
          quantity,
          observation: item.observation ?? null,
        };
        this.comandaService.addItem(this.comandaForEdit!.id, dto).subscribe({
          next: () => this.reloadEditableComanda(),
          error: (err) => {
            alert(err?.error?.message || 'Não foi possível atualizar a quantidade do item.');
            this.loadingChangeItem = false;
          },
        });
      },
      error: (err) => {
        alert(err?.error?.message || 'Não foi possível atualizar a quantidade do item.');
        this.loadingChangeItem = false;
      },
    });
  }

  closeAddItemsModal(): void {
    this.addItemsModalVisible = false;
    this.comandaForEdit = null;
    this.resetProductSelection();
  }

  private reloadEditableComanda(): void {
    if (!this.comandaForEdit) return;
    this.comandaService.find(this.comandaForEdit.id).subscribe({
      next: (res) => {
        this.comandaForEdit = res?.data ?? this.comandaForEdit;
        this.loadingChangeItem = false;
        this.refreshTablesData();
      },
      error: () => {
        this.loadingChangeItem = false;
      },
    });
  }

  private resetProductSelection(): void {
    this.productSearch = '';
    this.productSearchResults = [];
    this.selectedProduct = null;
    this.itemQuantity = 1;
    this.itemObservation = '';
  }

  private refreshTablesData(): void {
    this.comandaService.list('ABERTA', undefined, 1).subscribe({
      next: (res) => {
        const comandas = res?.data ?? [];
        this.tablesEnriched = this.tablesEnriched.map((row) => {
          const tableComanda = comandas.find((c) => c.tables?.some((t) => t.id === row.table.id));
          return {
            ...row,
            comandaId: tableComanda?.id ?? null,
            comandaTotal: tableComanda?.total,
            comandaObservation: tableComanda?.observation ?? null,
            table: {
              ...row.table,
              status: tableComanda ? 'OCUPADA' : row.table.status,
            },
          };
        });
        this.applyFilter();
      },
      error: () => {
        // mantém estado atual em caso de erro
      },
    });
  }

  /** Consultar comanda: busca por mesa e abre modal com itens e total. */
  consultarComanda(): void {
    if (!this.selectedTable || this.loadingConsulta) return;
    const tableId = this.selectedTable.table.id;
    this.mesaNumberForModal = this.selectedTable.table.number;
    this.loadingConsulta = true;
    this.comandaForModal = null;

    this.comandaService.findByTable(tableId).subscribe({
      next: (res: RestResponse<Comanda>) => {
        this.comandaForModal = res?.data ?? null;
        this.comandaModalVisible = !!this.comandaForModal;
        if (!this.comandaForModal) {
          alert('Nenhuma comanda aberta para esta mesa.');
        }
        this.loadingConsulta = false;
      },
      error: () => {
        alert('Nenhuma comanda aberta para esta mesa.');
        this.loadingConsulta = false;
      },
    });
  }

  closeComandaModal(): void {
    this.comandaModalVisible = false;
    this.comandaForModal = null;
  }

  private mapComandaToSale(comanda: Comanda): CreateSaleFromPdvDto {
    const shiftId = localStorage.getItem('pdvId');
    // API pode retornar "items" ou "itens"
    const rawItems = comanda.items ?? (comanda as any).itens ?? [];
    const items = Array.isArray(rawItems) ? rawItems : [];
    return {
      customerName: comanda.observation ?? '',
      customerId: null,
      items: items.map((item: any) => ({
        productId: item.productId,
        productName: item.productName ?? item.nomeProduto ?? '',
        amount: item.quantity ?? item.amount ?? item.quantidade ?? 0,
        productPrice: item.unitPrice ?? item.productPrice ?? item.precoUnitario ?? 0,
        total: item.total ?? (item.quantity ?? item.amount ?? 0) * (item.unitPrice ?? item.productPrice ?? 0),
        productBarCode: item.productBarCode ?? item.barCode ?? '',
        productCode: item.productCode ?? item.code ?? '',
        costPrice: item.costPrice ?? item.precoCusto ?? 0,
      })),
      discountTotal: comanda.discount ?? 0,
      exchange: 0,
      grossTotal: comanda.subtotal ?? 0,
      netTotal: comanda.total ?? 0,
      shiftId: shiftId ? parseInt(shiftId, 10) : null,
    } as CreateSaleFromPdvDto;
  }

  closeView(): void {
    this.closeViewRequest.emit();
  }
}
